/**
 * configDraft — persist the cloud Setup working draft across a page refresh (#86bbm58f7).
 *
 * The device editor keeps `form` (the user's staged edits) vs `pristine` (the server snapshot) in React
 * state, so a browser refresh drops every staged edit. This module mirrors the working form to
 * sessionStorage per device and restores it on reload — WITH a staleness guard:
 *
 *   A draft is restored ONLY when it was made against the SAME server config it is being restored onto
 *   (isDeviceDirty(draft.base, pristine) === false). If the device's config changed underneath the draft
 *   (someone else edited it, or a conf_rev bump), the draft is DISCARDED rather than restored over — the
 *   user never clobbers newer server state with an edit built on an outdated base.
 *
 * Read-only device facts (ip / online / lastEvent / …) always come from the FRESH load; the draft only
 * owns the EDITABLE fields (name / customer / site / config). Everything here is pure except the
 * sessionStorage I/O, which is fully guarded (quota / unavailable / corrupt JSON are non-fatal).
 * Device configs can contain credential-bearing RTSP URLs, so drafts must clear when the tab closes.
 */
import { isDeviceDirty } from './configDirty';

const PREFIX = 'dl_setup_draft_';
const keyFor = (deviceId) => PREFIX + deviceId;
const store = () => (typeof sessionStorage !== 'undefined' ? sessionStorage : null);

// Remove drafts written by older builds. Never migrate them because their full configs can contain
// camera credentials that must not remain in persistent browser storage.
const clearLegacyDraft = (deviceId) => {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(keyFor(deviceId));
  } catch (e) { /* unavailable — ignore */ }
};

// The fields the user edits — the only ones a draft restores. Everything else (read-only device facts)
// is taken from the fresh server load so a restored draft never shows stale online/last-event/ip.
const EDITABLE = ['name', 'customerId', 'projectId', 'config', 'cfg', 'conn'];

/** Fresh server form + the draft's editable edits laid on top. */
const mergeDraft = (fresh, draftForm) => {
  const out = { ...fresh };
  for (const k of EDITABLE) out[k] = draftForm[k];
  return out;
};

/**
 * Persist the working form as a draft, keyed by device. Stores the `base` (the pristine it was made
 * against) so the restore can prove the server hasn't changed underneath it. A CLEAN form (no effective
 * change vs base — e.g. a hand-revert) clears the entry instead of persisting a no-op.
 * @param {string} deviceId - sDeviceID
 * @param {Object} base - the pristine snapshot the edits are relative to
 * @param {Object} form - the working form
 */
export const saveDraft = (deviceId, base, form) => {
  if (!deviceId || !base || !form) return;
  try {
    clearLegacyDraft(deviceId);
    const s = store();
    if (!s) return;
    if (!isDeviceDirty(form, base)) { s.removeItem(keyFor(deviceId)); return; }
    s.setItem(keyFor(deviceId), JSON.stringify({ base, form }));
  } catch (e) { /* quota / storage unavailable — non-fatal, the refresh just won't restore */ }
};

/**
 * The restorable form for a device, or null. Restores ONLY when the draft's base still matches the
 * current server pristine (else the draft is stale → removed and null). The returned form carries the
 * FRESH read-only facts with the draft's editable edits applied.
 * @param {string} deviceId - sDeviceID
 * @param {Object} pristine - the current server snapshot just loaded
 * @returns {Object|null} a ready-to-use form, or null
 */
export const loadDraft = (deviceId, pristine) => {
  if (!deviceId || !pristine) return null;
  try {
    clearLegacyDraft(deviceId);
    const s = store();
    if (!s) return null;
    const raw = s.getItem(keyFor(deviceId));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || !draft.base || !draft.form) { s.removeItem(keyFor(deviceId)); return null; }
    // Staleness guard: the server config must be UNCHANGED since the draft was captured.
    if (isDeviceDirty(draft.base, pristine)) { s.removeItem(keyFor(deviceId)); return null; }
    // Nothing worth restoring if the draft no longer differs from the server state.
    if (!isDeviceDirty(draft.form, pristine)) { s.removeItem(keyFor(deviceId)); return null; }
    return mergeDraft(pristine, draft.form);
  } catch (e) {
    try { const s = store(); if (s) s.removeItem(keyFor(deviceId)); } catch (_) { /* ignore */ }
    return null;
  }
};

/** Drop a device's persisted draft (on push, revert, or a form gone clean). */
export const clearDraft = (deviceId) => {
  if (!deviceId) return;
  try { const s = store(); if (s) s.removeItem(keyFor(deviceId)); } catch (e) { /* ignore */ }
  clearLegacyDraft(deviceId);
};

/** Drop ALL persisted setup drafts (logout / session end). */
export const clearAllDrafts = () => {
  const clearStore = (s) => {
    if (!s) return;
    for (let i = s.length - 1; i >= 0; i--) {
      const k = s.key(i);
      if (k && k.indexOf(PREFIX) === 0) s.removeItem(k);
    }
  };
  try { clearStore(store()); } catch (e) { /* ignore */ }
  try { clearStore(typeof localStorage !== 'undefined' ? localStorage : null); } catch (e) { /* ignore */ }
};
